import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TvIcon from '@mui/icons-material/Tv';
import { useSnackbar } from 'notistack';
import { authService } from '../api/auth';
import { ChangePasswordRequest } from '../types/requests/auth.requests';
import { UserSession } from '../types/entities/session.entity';
import { formatAppDateTime } from '../utils/dateFormats';
import { generatePassword } from '../utils/generatePassword';

type PasswordField = 'current_password' | 'password' | 'password_confirmation';

type PasswordFormState = Record<PasswordField, string>;

type PasswordFormErrors = Partial<Record<PasswordField, string>>;

const emptyForm = (): PasswordFormState => ({
    current_password: '',
    password: '',
    password_confirmation: '',
});

const formatSessionDate = (value: string | null, emptyLabel: string): string => {
    if (!value) {
        return emptyLabel;
    }
    return formatAppDateTime(value) || emptyLabel;
};

export const Account = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { data: user, isLoading, error } = useQuery({
        queryKey: ['user'],
        queryFn: authService.getMe,
    });

    const {
        data: sessions = [],
        isLoading: sessionsLoading,
        error: sessionsError,
    } = useQuery<UserSession[]>({
        queryKey: ['user-sessions'],
        queryFn: authService.getSessions,
    });

    const [formData, setFormData] = useState<PasswordFormState>(emptyForm);
    const [errors, setErrors] = useState<PasswordFormErrors>({});
    const [showPassword, setShowPassword] = useState<Record<PasswordField, boolean>>({
        current_password: false,
        password: false,
        password_confirmation: false,
    });

    useEffect(() => {
        document.title = t('account.title');
    }, [t]);

    const validateField = (field: PasswordField, value: string, data: PasswordFormState): string => {
        if (!value) {
            return t('validation.required');
        }
        if (field === 'password' && value.length < 8) {
            return t('validation.password.minLength');
        }
        if (field === 'password_confirmation' && value !== data.password) {
            return t('account.passwordMismatch');
        }
        return '';
    };

    const validateForm = (data: PasswordFormState): PasswordFormErrors => {
        const nextErrors: PasswordFormErrors = {};
        (Object.keys(data) as PasswordField[]).forEach((field) => {
            const message = validateField(field, data[field], data);
            if (message) {
                nextErrors[field] = message;
            }
        });
        return nextErrors;
    };

    const changePasswordMutation = useMutation({
        mutationFn: (payload: ChangePasswordRequest) => authService.changePassword(payload),
        onSuccess: () => {
            setFormData(emptyForm());
            setErrors({});
            enqueueSnackbar(t('account.changePasswordSuccess'), { variant: 'success' });
        },
        onError: (err: unknown) => {
            if (axios.isAxiosError(err) && err.response?.status === 422) {
                const apiErrors = err.response.data?.errors as Record<string, string[]> | undefined;
                if (apiErrors) {
                    const nextErrors: PasswordFormErrors = {};
                    (Object.keys(apiErrors) as PasswordField[]).forEach((field) => {
                        if (apiErrors[field]?.[0]) {
                            nextErrors[field] = apiErrors[field][0];
                        }
                    });
                    setErrors(nextErrors);
                }
            }
            enqueueSnackbar(t('account.changePasswordError'), { variant: 'error' });
        },
    });

    const revokeSessionMutation = useMutation({
        mutationFn: (id: string) => authService.revokeSession(id),
        onSuccess: async (revokedCurrent) => {
            await queryClient.invalidateQueries({ queryKey: ['user-sessions'] });
            if (revokedCurrent) {
                await authService.logout();
                queryClient.removeQueries({ queryKey: ['user'] });
                navigate('/login');
                enqueueSnackbar(t('account.sessionRevokeCurrentSuccess'), { variant: 'success' });
                return;
            }
            enqueueSnackbar(t('account.sessionRevokeSuccess'), { variant: 'success' });
        },
        onError: () => {
            enqueueSnackbar(t('account.sessionRevokeError'), { variant: 'error' });
        },
    });

    const revokeOtherSessionsMutation = useMutation({
        mutationFn: () => authService.revokeOtherSessions(),
        onSuccess: async (revokedCount) => {
            await queryClient.invalidateQueries({ queryKey: ['user-sessions'] });
            if (revokedCount === 0) {
                enqueueSnackbar(t('account.revokeOtherSessionsNone'), { variant: 'info' });
                return;
            }
            enqueueSnackbar(t('account.revokeOtherSessionsSuccess', { count: revokedCount }), { variant: 'success' });
        },
        onError: () => {
            enqueueSnackbar(t('account.revokeOtherSessionsError'), { variant: 'error' });
        },
    });

    const currentIsBrowser = sessions.some((session) => session.is_current && session.kind === 'session');
    const otherBrowserSessionsCount = sessions.filter((session) => {
        if (session.kind !== 'session') {
            return false;
        }
        return currentIsBrowser ? !session.is_current : true;
    }).length;
    const sessionsActionPending = revokeSessionMutation.isPending || revokeOtherSessionsMutation.isPending;

    const sessionMeta = (value: string | null | undefined) => {
        const trimmed = value?.trim();
        if (!trimmed) {
            return t('account.sessionUnknown');
        }
        if (trimmed === 'Local') {
            return t('account.sessionLocal');
        }
        return trimmed;
    };

    const handleFieldChange = (field: PasswordField) => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const nextData = { ...formData, [field]: value };
        setFormData(nextData);
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: validateField(field, value, nextData) }));
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const nextErrors = validateForm(formData);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            return;
        }
        changePasswordMutation.mutate(formData);
    };

    const handleGeneratePassword = async () => {
        const nextPassword = generatePassword();
        setFormData((prev) => ({
            ...prev,
            password: nextPassword,
            password_confirmation: nextPassword,
        }));
        setErrors((prev) => ({
            ...prev,
            password: '',
            password_confirmation: '',
        }));
        setShowPassword((prev) => ({
            ...prev,
            password: true,
            password_confirmation: true,
        }));

        try {
            await navigator.clipboard.writeText(nextPassword);
            enqueueSnackbar(t('account.generatePasswordSuccess'), { variant: 'success' });
        } catch (_err) {
            enqueueSnackbar(t('account.generatePasswordError'), { variant: 'warning' });
        }
    };

    const toggleVisibility = (field: PasswordField) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const passwordAdornment = (field: PasswordField) => (
        <InputAdornment position="end">
            <IconButton
                aria-label={t('account.togglePasswordVisibility')}
                onClick={() => toggleVisibility(field)}
                edge="end"
            >
                {showPassword[field] ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
        </InputAdornment>
    );

    if (isLoading) {
        return null;
    }

    if (error || !user) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{t('common.errorFetchingData')}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                {t('account.title')}
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {t('account.profile')}
                </Typography>
                <Stack spacing={1}>
                    <Typography variant="body1">
                        <strong>{t('account.name')}:</strong> {user.name}
                    </Typography>
                    <Typography variant="body1">
                        <strong>{t('account.email')}:</strong> {user.email}
                    </Typography>
                </Stack>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {t('account.changePassword')}
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={2}>
                        <TextField
                            label={t('account.currentPassword')}
                            name="current_password"
                            type={showPassword.current_password ? 'text' : 'password'}
                            autoComplete="current-password"
                            value={formData.current_password}
                            onChange={handleFieldChange('current_password')}
                            error={!!errors.current_password}
                            helperText={errors.current_password}
                            fullWidth
                            InputProps={{ endAdornment: passwordAdornment('current_password') }}
                        />
                        <TextField
                            label={t('account.newPassword')}
                            name="password"
                            type={showPassword.password ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={formData.password}
                            onChange={handleFieldChange('password')}
                            error={!!errors.password}
                            helperText={errors.password || t('validation.password.minLength')}
                            fullWidth
                            InputProps={{ endAdornment: passwordAdornment('password') }}
                        />
                        <TextField
                            label={t('account.confirmPassword')}
                            name="password_confirmation"
                            type={showPassword.password_confirmation ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={formData.password_confirmation}
                            onChange={handleFieldChange('password_confirmation')}
                            error={!!errors.password_confirmation}
                            helperText={errors.password_confirmation}
                            fullWidth
                            InputProps={{ endAdornment: passwordAdornment('password_confirmation') }}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button
                                type="button"
                                variant="outlined"
                                startIcon={<AutoFixHighIcon />}
                                onClick={() => void handleGeneratePassword()}
                            >
                                {t('account.generatePassword')}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={changePasswordMutation.isPending}
                            >
                                {t('account.updatePassword')}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    {t('account.approveDevice')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('account.approveDeviceHelper')}
                </Typography>
                <Button
                    component={RouterLink}
                    to="/link"
                    variant="outlined"
                    startIcon={<TvIcon />}
                    sx={{ textTransform: 'none' }}
                >
                    {t('account.approveDeviceAction')}
                </Button>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ mb: 1 }}
                >
                    <Typography variant="h6">{t('account.sessions')}</Typography>
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={sessionsLoading || sessionsActionPending || otherBrowserSessionsCount === 0}
                        onClick={() => revokeOtherSessionsMutation.mutate()}
                    >
                        {t('account.revokeOtherSessions')}
                    </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('account.sessionsHelper')}
                </Typography>

                {sessionsLoading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={28} />
                    </Box>
                ) : sessionsError ? (
                    <Alert severity="error">{t('common.errorFetchingData')}</Alert>
                ) : sessions.length === 0 ? (
                    <Typography color="text.secondary">{t('account.noSessions')}</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('apiTokens.name')}</TableCell>
                                    <TableCell>{t('account.sessionDevice')}</TableCell>
                                    <TableCell>{t('account.sessionIp')}</TableCell>
                                    <TableCell>{t('account.sessionCountry')}</TableCell>
                                    <TableCell>{t('account.sessionCreated')}</TableCell>
                                    <TableCell>{t('account.sessionLastUsed')}</TableCell>
                                    <TableCell>{t('account.sessionExpires')}</TableCell>
                                    <TableCell align="right">{t('apiTokens.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sessions.map((session) => (
                                    <TableRow key={session.id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                                <Typography variant="body2">{session.label}</Typography>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        session.kind === 'session'
                                                            ? t('account.sessionKindBrowser')
                                                            : t('account.sessionKindApi')
                                                    }
                                                    variant="outlined"
                                                />
                                                {session.is_current ? (
                                                    <Chip size="small" color="primary" label={t('account.sessionCurrent')} />
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {session.user_agent ? (
                                                <Tooltip title={`${t('account.sessionUserAgent')}: ${session.user_agent}`}>
                                                    <Typography variant="body2" component="span">
                                                        {sessionMeta(session.device_label)}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                sessionMeta(session.device_label)
                                            )}
                                        </TableCell>
                                        <TableCell>{sessionMeta(session.ip_address)}</TableCell>
                                        <TableCell>{sessionMeta(session.country)}</TableCell>
                                        <TableCell>
                                            {formatSessionDate(session.created_at, t('account.sessionNeverUsed'))}
                                        </TableCell>
                                        <TableCell>
                                            {formatSessionDate(session.last_used_at, t('account.sessionNeverUsed'))}
                                        </TableCell>
                                        <TableCell>
                                            {formatSessionDate(session.expires_at, t('account.sessionNoExpiry'))}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                color={session.is_current ? 'inherit' : 'error'}
                                                disabled={sessionsActionPending}
                                                onClick={() => revokeSessionMutation.mutate(session.id)}
                                            >
                                                {session.is_current
                                                    ? t('account.sessionRevokeCurrent')
                                                    : t('account.sessionRevoke')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Container>
    );
};
